import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { listarConversas } from "../services/chat";

/**
 * Mantém document.title como "(N) Nome do app" enquanto há mensagens
 * não lidas DE CHAT. Notificações de projetos não pesam aqui (ficam só
 * no sino). Quando a aba está em background, alterna o título a cada
 * 1.5s entre "(N) Nova mensagem" e o título normal, pra chamar atenção.
 */
export function useTituloAba(nomeBase: string) {
  const qc = useQueryClient();
  const piscandoRef = useRef<{ timer: number | null; alternar: boolean }>({ timer: null, alternar: false });

  useEffect(() => {
    let cancelado = false;

    async function ciclo() {
      // Só conta chat — notificações de projeto não entram no título da aba.
      let totalChat = 0;
      try {
        const conversas = await qc.fetchQuery({ queryKey: ["chat-conversas"], queryFn: listarConversas, staleTime: 5000 });
        totalChat = (conversas || []).reduce((s: number, c: any) => s + (c.naoLidas || 0), 0);
      } catch {}

      if (cancelado) return;
      atualizarTitulo(totalChat, totalChat > 0, nomeBase);
    }

    function atualizarTitulo(total: number, temChat: boolean, base: string) {
      // Limpa timer antigo
      if (piscandoRef.current.timer) {
        window.clearInterval(piscandoRef.current.timer);
        piscandoRef.current.timer = null;
      }

      if (total === 0) {
        document.title = base;
        return;
      }

      // Se aba ativa: mostra prefixo fixo
      if (!document.hidden) {
        document.title = `(${total}) ${base}`;
        return;
      }

      // Aba em background: pisca
      const tituloA = `(${total}) ${temChat ? "Nova mensagem" : "Nova notificação"}`;
      const tituloB = base;
      let alterna = false;
      document.title = tituloA;
      piscandoRef.current.timer = window.setInterval(() => {
        document.title = alterna ? tituloA : tituloB;
        alterna = !alterna;
      }, 1500);
    }

    function onMensagem() { ciclo(); }
    function onVisibility() { ciclo(); }

    window.addEventListener("growsoft:chat-mensagem", onMensagem);
    document.addEventListener("visibilitychange", onVisibility);

    ciclo();
    const intervalo = window.setInterval(ciclo, 30_000);

    return () => {
      cancelado = true;
      window.removeEventListener("growsoft:chat-mensagem", onMensagem);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalo);
      if (piscandoRef.current.timer) window.clearInterval(piscandoRef.current.timer);
      document.title = nomeBase;
    };
  }, [nomeBase, qc]);
}
